import { Popover, PopoverContent, PopoverPortal, PopoverTrigger } from "@radix-ui/react-popover";
import moment from "moment";
import React, { useState } from "react";
import InlineSVG from "react-inlinesvg";
import { useToast } from "src/components/ToastWrapper/index.js";
import { cn } from "src/lib/cn";
import { SudoTooltip } from "src/sudokit/components/SudoTooltip";
import { Text } from "src/sudokit/components/Text";
import { WithReset } from "src/sudokit/Layout/BoxReset";
import { PartnerCode } from "./PartnerCodesQueries";

interface PartnerCodesTableProps {
  partnerCodes: PartnerCode[];
  onEdit: (code: PartnerCode) => void;
  onDelete: (code: PartnerCode) => void;
  onViewDetails: (code: PartnerCode) => void;
}

type SortField = "code" | "type" | "credit_amount" | "clicks" | "signups" | "conversions" | "conversionRate" | "created_at" | "max_signup_count" | "partner_name";
type SortDirection = "asc" | "desc";

export const PartnerCodesTable: React.FC<PartnerCodesTableProps> = ({ partnerCodes, onEdit, onDelete, onViewDetails }) => {
  const [sortField, setSortField] = useState<SortField>("created_at");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const { setToast } = useToast();

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const sortedCodes = [...partnerCodes].sort((a, b) => {
    let aValue: number | string;
    let bValue: number | string;

    switch (sortField) {
      case "code":
        aValue = a.code.toLowerCase();
        bValue = b.code.toLowerCase();
        break;
      case "type":
        aValue = a.type;
        bValue = b.type;
        break;
      case "credit_amount":
        aValue = a.credit_amount;
        bValue = b.credit_amount;
        break;
      case "clicks":
        aValue = a.click_count;
        bValue = b.click_count;
        break;
      case "signups":
        aValue = a.signup_count;
        bValue = b.signup_count;
        break;
      case "conversions":
        aValue = a.conversion_count;
        bValue = b.conversion_count;
        break;
      case "conversionRate":
        aValue = a.stats?.conversionRate || 0;
        bValue = b.stats?.conversionRate || 0;
        break;
      case "max_signup_count":
        aValue = a.max_signup_count;
        bValue = b.max_signup_count;
        break;
      case "partner_name":
        aValue = a.metadata?.partner_name?.toLowerCase() || "";
        bValue = b.metadata?.partner_name?.toLowerCase() || "";
        break;
      case "created_at":
        aValue = new Date(a.created_at).getTime();
        bValue = new Date(b.created_at).getTime();
        break;
      default:
        return 0;
    }

    if (aValue > bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue < bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return (
        <InlineSVG
          src="/assets/down-arrow.svg"
          className="w-2 h-2 opacity-0 group-hover:opacity-30 transition-opacity [&_path]:fill-app-grey-2"
        />
      );
    }
    return (
      <InlineSVG
        src="/assets/down-arrow.svg"
        className={cn("w-2 h-2 transition-transform [&_path]:fill-app-primary-cta-high-contrast", sortDirection === "desc" && "rotate-180")}
      />
    );
  };

  return (
    <div className="rounded-xl bg-app-menu-color border border-app-menu-color-hover shadow-card overflow-auto scrollbar-track-transparent scrollbar-corner-transparent scrollbar-thumb-slate-400">
      <table className="min-w-full divide-y divide-app-menu-color-hover">
        <thead className="bg-app-menu-color h-9">
          <tr className="border-b border-app-menu-color-hover">
            <th
              scope="col"
              className="group px-4 py-4 text-left text-xs font-semibold text-app-grey-1 uppercase tracking-wider cursor-pointer hover:text-app-grey-0 transition-colors"
              onClick={() => handleSort("code")}
            >
              <div className="flex items-center gap-2">
                <span>Code</span>
                <SortIcon field="code" />
              </div>
            </th>
            <th
              scope="col"
              className="group px-4 py-4 text-left text-xs font-semibold text-app-grey-1 uppercase tracking-wider cursor-pointer hover:text-app-grey-0 transition-colors"
              onClick={() => handleSort("type")}
            >
              <div className="flex items-center gap-2">
                <span>Type</span>
                <SortIcon field="type" />
              </div>
            </th>
            <th
              scope="col"
              className="px-4 py-4 text-left text-xs font-semibold text-app-grey-1 uppercase tracking-wider"
            >
              Status
            </th>
            <th
              scope="col"
              className="group px-4 py-4 text-left text-xs font-semibold text-app-grey-1 uppercase tracking-wider cursor-pointer hover:text-app-grey-0 transition-colors"
              onClick={() => handleSort("partner_name")}
            >
              <div className="flex items-center gap-2">
                <span>Partner Name</span>
                <SortIcon field="partner_name" />
              </div>
            </th>
            <th
              scope="col"
              className="group px-4 py-4 text-left text-xs font-semibold text-app-grey-1 uppercase tracking-wider cursor-pointer hover:text-app-grey-0 transition-colors"
              onClick={() => handleSort("credit_amount")}
            >
              <div className="flex items-center gap-2">
                <span>Credits</span>
                <SortIcon field="credit_amount" />
              </div>
            </th>
            <th
              scope="col"
              className="group px-4 py-4 text-left text-xs font-semibold text-app-grey-1 uppercase tracking-wider cursor-pointer hover:text-app-grey-0 transition-colors"
              onClick={() => handleSort("clicks")}
            >
              <div className="flex items-center gap-2">
                <span>Clicks</span>
                <SortIcon field="clicks" />
              </div>
            </th>
            <th
              scope="col"
              className="group px-4 py-4 text-left text-xs font-semibold text-app-grey-1 uppercase tracking-wider cursor-pointer hover:text-app-grey-0 transition-colors"
              onClick={() => handleSort("signups")}
            >
              <div className="flex items-center gap-2">
                <span>Signups</span>
                <SortIcon field="signups" />
              </div>
            </th>
            <th
              scope="col"
              className="group px-4 py-4 text-left text-xs font-semibold text-app-grey-1 uppercase tracking-wider cursor-pointer hover:text-app-grey-0 transition-colors"
              onClick={() => handleSort("max_signup_count")}
            >
              <div className="flex items-center gap-2">
                <span>Limit</span>
                <SortIcon field="max_signup_count" />
              </div>
            </th>
            <th
              scope="col"
              className="group px-4 py-4 text-left text-xs font-semibold text-app-grey-1 uppercase tracking-wider cursor-pointer hover:text-app-grey-0 transition-colors"
              onClick={() => handleSort("conversions")}
            >
              <div className="flex items-center gap-2">
                <span>Conversions</span>
                <SortIcon field="conversions" />
              </div>
            </th>
            <th
              scope="col"
              className="group px-4 py-4 text-left text-xs font-semibold text-app-grey-1 uppercase tracking-wider cursor-pointer hover:text-app-grey-0 transition-colors"
              onClick={() => handleSort("conversionRate")}
            >
              <div className="flex items-center gap-2">
                <span>Conv. %</span>
                <SortIcon field="conversionRate" />
              </div>
            </th>
            <th
              scope="col"
              className="group px-4 py-4 text-left text-xs font-semibold text-app-grey-1 uppercase tracking-wider cursor-pointer hover:text-app-grey-0 transition-colors"
              onClick={() => handleSort("created_at")}
            >
              <div className="flex items-center gap-2">
                <span>Created</span>
                <SortIcon field="created_at" />
              </div>
            </th>
            <th
              scope="col"
              className="relative px-4 py-4"
            >
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedCodes.map((code) => (
            <tr
              key={code.id}
              className="hover:bg-gray-100 transition-colors"
            >
              <td className="px-4 py-4 whitespace-nowrap">
                <WithReset
                  onClick={() => onViewDetails(code)}
                  className="text-sm font-medium text-app-primary-cta-high-contrast cursor-pointer hover:brightness-110 transition-all"
                >
                  {code.code}
                </WithReset>
              </td>
              <td className="px-4 py-4 whitespace-nowrap">
                <span
                  className={cn("inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold", code.type === "partner" ? "bg-blue-500/10 text-blue-500" : "bg-purple-500/10 text-purple-500")}
                >
                  {code.type.charAt(0).toUpperCase() + code.type.slice(1)}
                </span>
              </td>
              <td className="px-4 py-4 whitespace-nowrap">
                <span
                  className={cn("inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold", code.is_active ? "bg-green-500/10 text-green-500" : "bg-app-grey-3/30 text-app-grey-1")}
                >
                  <span className={cn("w-1.5 h-1.5 rounded-full", code.is_active ? "bg-green-500" : "bg-app-grey-2")} />
                  {code.is_active ? "Active" : "Inactive"}
                </span>
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-sm text-app-grey-0">
                {code.metadata?.partner_name ? (
                  <span className="font-medium">{code.metadata.partner_name}</span>
                ) : (
                  <span className="text-app-grey-1 italic">No name set</span>
                )}
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-sm text-app-grey-0 font-medium">{code.credit_amount.toLocaleString()}</td>
              <td className="px-4 py-4 whitespace-nowrap text-sm text-app-grey-0">{code.click_count.toLocaleString()}</td>
              <td className="px-4 py-4 whitespace-nowrap text-sm text-app-grey-0">{code.signup_count.toLocaleString()}</td>
              <td className="px-4 py-4 whitespace-nowrap text-sm">
                {code.max_signup_count === 0 ? (
                  <span className="text-app-grey-1 italic">Unlimited</span>
                ) : (
                  <span className="text-app-grey-0 font-medium">{code.max_signup_count?.toLocaleString() || "∞"}</span>
                )}
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-sm text-app-grey-0">{code.conversion_count.toLocaleString()}</td>
              <td className="px-4 py-4 whitespace-nowrap text-sm">
                <span className={cn("font-medium", code.stats?.conversionRate && code.stats.conversionRate > 5 ? "text-green-500" : "text-app-grey-0")}>
                  {code.stats?.conversionRate ? `${code.stats.conversionRate}%` : "0%"}
                </span>
              </td>
              <td className="px-4 py-2 whitespace-nowrap text-sm text-app-grey-1">{moment(code.created_at).format("MMM D, YYYY")}</td>

              <td className="items-end justify-start flex h-full pr-3">
                <Popover>
                  <PopoverTrigger asChild>
                    <WithReset className="hover:bg-app-grey-3 p-2 translate-y-1.5 rounded-lg cursor-pointer transition-all hover:scale-[1.02] active:scale-[0.98] flex gap-2 items-center justify-start rotate-90">
                      <InlineSVG
                        src="/assets/three-dots.svg"
                        className="h-5 w-5 [&_path]:fill-app-grey-0"
                      />
                    </WithReset>
                  </PopoverTrigger>
                  <PopoverPortal>
                    <PopoverContent className="animate-flyout bg-app-menu-color p-1 rounded-xl shadow-md outline-none ring-0">
                      <WithReset
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/${code.type === "partner" ? "partners" : "events"}/${code.code}`);
                          setToast("Copied the link to your clipboard");
                        }}
                        className="w-full h-full hover:bg-app-grey-3 pr-3 pl-2 py-1 rounded-lg cursor-pointer transition-all hover:scale-[1.02] active:scale-[0.98] flex gap-2 items-center justify-start"
                      >
                        <InlineSVG
                          src="/assets/chain-linked.svg"
                          className="h-5 w-5 [&_path]:fill-app-grey-0"
                        />
                        <Text size="sm">Copy Link</Text>
                      </WithReset>
                      <WithReset
                        onClick={() => {
                          navigator.clipboard.writeText(code.code);
                          setToast("Copied the code to your clipboard");
                        }}
                        className="w-full h-full hover:bg-app-grey-3 pr-3 pl-2 py-1 rounded-lg cursor-pointer transition-all hover:scale-[1.02] active:scale-[0.98] flex gap-2 items-center justify-start"
                      >
                        <InlineSVG
                          src="/assets/pencil.svg"
                          className="h-5 w-5 [&_path]:fill-app-grey-0"
                        />
                        <Text size="sm">Copy Code</Text>
                      </WithReset>

                      <WithReset
                        onClick={() => onEdit(code)}
                        className="w-full h-full hover:bg-app-grey-3 pr-3 pl-2 py-1 rounded-lg cursor-pointer transition-all hover:scale-[1.02] active:scale-[0.98] flex gap-2 items-center justify-start"
                      >
                        <InlineSVG
                          src="/assets/pencil.svg"
                          className="h-5 w-5 [&_path]:fill-app-grey-0"
                        />
                        <Text size="sm">Edit</Text>
                      </WithReset>

                      <SudoTooltip text={code.signup_count || code.click_count || code.conversion_count ? "Cannot delete code with conversions, signups, or clicks. Please deactivate instead." : ""}>
                        <WithReset
                          disabled
                          onClick={() => {
                            if (!code.signup_count && !code.click_count && !code.conversion_count) {
                              onDelete(code);
                            } else {
                              setToast("Cannot delete code with conversions, signups, or clicks. Please deactivate instead.");
                            }
                          }}
                          className={cn(
                            "w-full h-full hover:bg-app-danger-color-hover pr-3 pl-2 py-1 rounded-lg cursor-pointer transition-all hover:scale-[1.02] active:scale-[0.98] flex gap-2 items-center justify-start",
                            (code.signup_count || code.click_count || code.conversion_count) && "opacity-50 cursor-not-allowed hover:bg-app-danger-color-hover",
                          )}
                        >
                          <InlineSVG
                            src="/assets/delete.svg"
                            className="h-5 w-5 [&_path]:fill-app-danger-color"
                          />
                          <Text
                            size="sm"
                            color="danger-color"
                          >
                            Delete
                          </Text>
                        </WithReset>
                      </SudoTooltip>
                    </PopoverContent>
                  </PopoverPortal>
                </Popover>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
